import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getUsersToAddInAssessment,
  updateAssessment,
  deleteUserFromAssessment,
} from "../axios/axiosFunctions";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PersonIcon from "@mui/icons-material/Person";
import { AssessmentContext } from "../contexts/assessments";
import { DeleteConfirmationModal } from "./common";
import { getColumnsHome } from "../utils/homeTable";

const UsersAssessment = ({ rows, assessmentId }) => {
  const navigate = useNavigate();
  const { refetchAssessments } = useContext(AssessmentContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [selectOpt, setSelectOpt] = useState([]);
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [userIdToDelete, setUserIdToDelete] = useState(null);
  const [localRows, setLocalRows] = useState(rows);

  const handleEdit = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const handleDelete = (userId) => {
    setUserIdToDelete(userId);
    setOpenDialogDelete(true);
  };

  const handleCloseDialogDelete = () => {
    setOpenDialogDelete(false);
    setUserIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (!userIdToDelete || !assessmentId) return;

    const res = await deleteUserFromAssessment(assessmentId, userIdToDelete);

    if (!res || res.success === false) {
      setError(res?.msg || "Error while deleting user, please try later");
      setAlertSeverity("error");
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 4000);
      handleCloseDialogDelete();
      return;
    }

    // Update local rows state by filtering out the deleted user
    setLocalRows((prevRows) => prevRows.filter((row) => row.id !== userIdToDelete));

    refetchAssessments();

    setError("User removed successfully");
    setAlertSeverity("success");
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
    }, 2000);

    handleCloseDialogDelete();
  };

  const columns = getColumnsHome(handleEdit, handleDelete);

  useEffect(() => {
    if (assessmentId) {
      getUsersToAdd();
    }
  }, [assessmentId]);

  useEffect(() => {
    setLocalRows(rows);
  }, [rows]);

  const mapOptions = (usersArray) => {
    if (!usersArray || usersArray.length === 0) {
      setSelectOpt([]);
      return;
    }

    const tempOpt = usersArray.map((u, i) => ({
      label: `${u.firstName}${u.lastName ? ` ${u.lastName}` : ""}`,
      index: i,
    }));
    setSelectOpt(tempOpt);
  };

  const getUsersToAdd = async () => {
    const res = await getUsersToAddInAssessment(assessmentId);
    if (res) {
      setUsers(res.users);
      mapOptions(res.users);
    }
  };
  const handleCellClick = (e) => {
    const { row, colDef } = e;
    if (colDef.field === "lastName" || colDef.field === "firstName") {
      navigate(`/profile/${row.id}`);
    }
  };

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (usersList.length === 0) {
      setDialogOpen(false);
      return;
    }
    setUsersList([]);
    getUsersToAdd();
    setDialogOpen(false);
  };

  const handleAddUsers = async () => {
    if (usersList.length === 0) {
      handleDialogClose();
      return;
    }

    const usersToAdd = usersList.map((u) => u._id);
    const currentUsers = localRows.map((u) => u.id);

    const data = {
      users: usersToAdd.concat(currentUsers),
    };

    const res = await updateAssessment(assessmentId, data);
    if (!res) {
      setError("Error while saving please try later");
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 4000);
      return;
    }

    refetchAssessments();
    setError("Saved correctly");
    setAlertSeverity("success");
    setShowError(true);
    handleDialogClose();
    setTimeout(() => {
      setShowError(false);
    }, 2000);
  };

  const handleChange = (e, newValue) => {
    if (!newValue) return;
    const { index } = newValue;
    const newUsersList = [...usersList];
    const selectedElemet = users[index];

    const newUsersToAdd = [...users.slice(0, index), ...users.slice(index + 1)];

    newUsersList.push(selectedElemet);
    setUsers(newUsersToAdd);
    mapOptions(newUsersToAdd);
    setUsersList(newUsersList);
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={500} color="secondary">
        Users
      </Typography>
      <DataGrid
        columns={columns}
        rows={localRows}
        onCellClick={handleCellClick}
        sx={{ marginTop: "30px", marginBottom: "30px" }}
        showToolbar
      />
      <Button
        onClick={handleDialogOpen}
        variant="contained"
        sx={{ color: "white" }}
      >
        Add User
      </Button>
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h5" fontWeight={500} color="secondary">
            Add Users
          </Typography>
          <Autocomplete
            onChange={handleChange}
            options={selectOpt}
            renderInput={(params) => <TextField {...params} label="Add user" />}
          />
          <Divider />
          {usersList.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                fontWeight={500}
                color="secondary"
                sx={{ marginTop: "20px" }}
              >
                Users to Add
              </Typography>
              <List>
                {usersList.map((u, i) => (
                  <ListItem key={i}>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${u.firstName}${u.lastName ? ` ${u.lastName}` : ""}`}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
          {showError && (
            <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>
              {error}
            </Alert>
          )}
          <Box sx={{ marginTop: "20px" }}>
            <Button
              onClick={handleAddUsers}
              variant="contained"
              sx={{ color: "white" }}
            >
              ADD USERS
            </Button>
            <Button
              onClick={handleDialogClose}
              variant="contained"
              sx={{ color: "white", marginLeft: "20px" }}
            >
              CANCEL
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <DeleteConfirmationModal
        openDialog={openDialogDelete}
        closeDialog={handleCloseDialogDelete}
        title="Delete User"
        message="Are you sure you want to delete this user from this assessment?"
        onConfirm={confirmDelete}
      />
      {showError && (
        <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default UsersAssessment;
